/* eslint-disable no-octal, no-octal-escape */

interface IDbConnectionStringKeyValuePair {
    key: string;
    value: string | null;
}

enum ParserState {
    NothingYet = 1, // start point
    Key,
    KeyEqual,
    KeyEnd,
    UnquotedValue,
    DoubleQuoteValue,
    DoubleQuoteValueQuote,
    SingleQuoteValue,
    SingleQuoteValueQuote,
    BraceQuoteValue,
    BraceQuoteValueQuote,
    QuotedValueEnd,
    NullTermination
}

export class DbConnectionStringBuilderService {
    private static readonly _parserStateEndError = new Error('ParserStateEndError');
    private static readonly _parserExitError = new Error('ParserExitError');

    // private static readonly _connectionStringQuoteValueRegex = new RegExp(`^[^\"'=;\\s\\p{Cc}]*$`); // generally do not quote the value if it matches the pattern
    // JavaScript RegExp doesn't support \p{Cc}
    private static readonly _connectionStringQuoteValueRegex = new RegExp(`^[^\"'=;\\s]*$`); // generally do not quote the value if it matches the pattern
    private static readonly _connectionStringQuoteOdbcValueRegex = new RegExp(`^\\{([^\\}\u0000]|\\}\\})*\\}$`); // do not quote odbc value if it matches this pattern

    private _keyValuePairs: { [keyword: string]: IDbConnectionStringKeyValuePair } = {};
    private readonly _useOdbcRules: boolean;

    public get connectionString(): string {
        let connStr = '';

        for (const keyword of Object.keys(this._keyValuePairs)) {
            const { key, value } = this._keyValuePairs[keyword];

            if (connStr.length > 0 && connStr[connStr.length - 1] !== ';') {
                connStr += ';';
            }

            connStr += this._useOdbcRules ? key : key.replace('=', '==');

            connStr += '=';

            if (value != null) {
                // else <keyword>=;
                if (this._useOdbcRules) {
                    if ((value.length > 0) &&
                        ((value[0] === '{') || (value.indexOf(';') >= 0) || (key.toLowerCase() === 'driver')) &&
                        !DbConnectionStringBuilderService._connectionStringQuoteOdbcValueRegex.test(value)) {
                        // always quote Driver value (required for ODBC Version 2.65 and earlier)
                        // always quote values that contain a ';'
                        connStr += `{${value.replace('}', '}}')}}`;
                    } else {
                        connStr += value;
                    }
                } else if (DbConnectionStringBuilderService._connectionStringQuoteValueRegex.test(value)) {
                    // <value> -> <value>
                    connStr += value;
                } else if (value.indexOf(`"`) !== -1 && value.indexOf(`'`) === -1) {
                    // <val"ue> -> <'val"ue'>
                    connStr += `'${value}'`;
                } else {
                    // <val'ue> -> <"val'ue">
                    // <=value> -> <"=value">
                    // <;value> -> <";value">
                    // < value> -> <" value">
                    // <va lue> -> <"va lue">
                    // <va'"lue> -> <"va'""lue">
                    connStr += `"${value.replace(`"`, `""`)}"`;
                }
            }
        }

        return connStr;
    }

    public constructor(useOdbcRules?: boolean) {
        this._useOdbcRules = !!useOdbcRules;
    }

    public parse(connectionString: string): void {
        this.clear();

        let position = 0;
        while (position < connectionString.length) {
            const result = this.getKeyValuePair(connectionString, position, this._useOdbcRules);
            position = result.position;

            if (result.keyName != null) {
                this.add(result.keyName, result.keyValue);
            } else {
                break;
            }
        }
    }

    public add(key: string, value: string | null): void {
        if (key == null || key.length === 0 || key[0] === ';' || key[0] === ' ' || key[key.length - 1] === ' ') {
            throw new Error('Invalid Key');
        }

        if (value != null && value.indexOf('\u0000') >= 0) {
            throw new Error('Invalid Value');
        }

        const keyword = key.toLowerCase();
        this._keyValuePairs[keyword] = {
            key,
            value
        };
    }

    public remove(key: string): void {
        const keyword = key.toLowerCase();
        delete this._keyValuePairs[keyword];
    }

    public clear(): void {
        this._keyValuePairs = {};
    }
    
    public getValueUsingAlias(key: string, aliasMap: {[key: string]: string[]}, throwError: boolean = true): string | null {
        let value: string;
        const aliasesOfKey = aliasMap[key];
        if (aliasesOfKey != null) {
            for (const aliasOfKey of aliasesOfKey) {
                const valueOfAliasKey = this.getValue(aliasOfKey, false);
                if (valueOfAliasKey != null) {
                    value = valueOfAliasKey;
                    break;
                }
            }
        } else {
            value = this.getValue(key, false);
        }

        if (value == null && throwError) {
            throw new Error('Key not found!');
        }
        return value;
    }

    public getValue(key: string, throwError: boolean = true): string | null {
        const keyword = key.toLowerCase();
        if (!this.contains(keyword)) {
            if (throwError) {
                throw new Error('Key not found!');
            } else {
                return null;
            }
        }

        return this._keyValuePairs[keyword].value;
    }

    public contains(key: string): boolean {
        const keyword = key.toLowerCase();
        return this._keyValuePairs[keyword] != null;
    }

    public getRemainedKeyValuePairs(keys: string[]): IDbConnectionStringKeyValuePair[] {
        keys.forEach((key) => this.remove(key));
        const remainedKeyValuePairs: IDbConnectionStringKeyValuePair[] = [];
        for (const keyword of Object.keys(this._keyValuePairs)) {
            remainedKeyValuePairs.push(this._keyValuePairs[keyword]);
        }
        return remainedKeyValuePairs;
    }

    public removeDefaultKeyValuePairs(pairs: IDbConnectionStringKeyValuePair[], key: string, defaultVal: string): void {
        const idx = pairs.findIndex((pair) => pair.key.toLowerCase() === key.toLowerCase() && pair.value === defaultVal);
        if (idx > -1) {
            pairs.splice(idx, 1);
        }
    }

    private getKeyValuePair(connectionString: string, currentPosition: number, useOdbcRules: boolean): {
        keyName: string | null;
        keyValue: string | null;
        position: number;
    } {
        let startposition = currentPosition;
        let buffer = '';

        let keyname: string | null = null;
        let keyvalue: string | null = null;

        let currentChar = '\0';

        let parserState: ParserState = (() => ParserState.NothingYet)();
        try {
            for (; currentPosition < connectionString.length; ++currentPosition) {
                currentChar = connectionString[currentPosition];

                try {
                    switch (parserState) {
                        case ParserState.NothingYet: // [\\s;]*
                            if ((';' === currentChar) || this.isWhiteSpace(currentChar)) {
                                continue;
                            }
                            if ('\0' === currentChar) {
                                parserState = ParserState.NullTermination;
                                continue;
                            }
                            if (this.isControl(currentChar)) {
                                throw this.createConnectionStringSyntaxError(startposition);
                            }
                            startposition = currentPosition;
                            if ('=' !== currentChar) {
                                parserState = ParserState.Key;
                                break;
                            } else {
                                parserState = ParserState.KeyEqual;
                                continue;
                            }

                        case ParserState.Key: // (?<key>([^=\\s\\p{Cc}]|\\s+[^=\\s\\p{Cc}]|\\s+==|==)+)
                            if ('=' === currentChar) {
                                parserState = ParserState.KeyEqual;
                                continue;
                            }
                            if (this.isWhiteSpace(currentChar)) {
                                break;
                            }
                            if (this.isControl(currentChar)) {
                                throw this.createConnectionStringSyntaxError(startposition);
                            }
                            break;

                        case ParserState.KeyEqual: // \\s*=(?!=)\\s*
                            if (!useOdbcRules && '=' === currentChar) {
                                parserState = ParserState.Key;
                                break;
                            }
                            keyname = this.getKeyName(buffer);
                            if (keyname == null || keyname === '') {
                                throw this.createConnectionStringSyntaxError(startposition);
                            }
                            buffer = '';
                            parserState = ParserState.KeyEnd;
                            throw DbConnectionStringBuilderService._parserStateEndError;

                        case ParserState.KeyEnd:
                            if (this.isWhiteSpace(currentChar)) {
                                continue;
                            }
                            if (useOdbcRules) {
                                if ('{' === currentChar) {
                                    parserState = ParserState.BraceQuoteValue;
                                    break;
                                }
                            } else {
                                if ('\'' === currentChar) {
                                    parserState = ParserState.SingleQuoteValue;
                                    continue;
                                }
                                if ('"' === currentChar) {
                                    parserState = ParserState.DoubleQuoteValue;
                                    continue;
                                }
                            }
                            if (';' === currentChar) {
                                throw DbConnectionStringBuilderService._parserExitError;
                            }
                            if ('\0' === currentChar) {
                                throw DbConnectionStringBuilderService._parserExitError;
                            }
                            if (this.isControl(currentChar)) {
                                throw this.createConnectionStringSyntaxError(startposition);
                            }
                            parserState = ParserState.UnquotedValue;
                            break;

                        case ParserState.UnquotedValue: // "((?![\"'\\s])" + "([^;\\s\\p{Cc}]|\\s+[^;\\s\\p{Cc}])*" + "(?<![\"']))"
                            if (this.isWhiteSpace(currentChar)) {
                                break;
                            }
                            if (this.isControl(currentChar) || ';' === currentChar) {
                                throw DbConnectionStringBuilderService._parserExitError;
                            }
                            break;

                        case ParserState.DoubleQuoteValue: // "(\"([^\"\u0000]|\"\")*\")"
                            if ('"' === currentChar) {
                                parserState = ParserState.DoubleQuoteValueQuote;
                                continue;
                            }
                            if ('\0' === currentChar) {
                                throw this.createConnectionStringSyntaxError(startposition);
                            }
                            break;

                        case ParserState.DoubleQuoteValueQuote:
                            if ('"' === currentChar) {
                                parserState = ParserState.DoubleQuoteValue;
                                break;
                            }
                            keyvalue = this.getKeyValue(buffer, false);
                            parserState = ParserState.QuotedValueEnd;
                            throw DbConnectionStringBuilderService._parserStateEndError;

                        case ParserState.SingleQuoteValue: // "('([^'\u0000]|'')*')"
                            if ('\'' === currentChar) {
                                parserState = ParserState.SingleQuoteValueQuote;
                                continue;
                            }
                            if ('\0' === currentChar) {
                                throw this.createConnectionStringSyntaxError(startposition);
                            }
                            break;

                        case ParserState.SingleQuoteValueQuote:
                            if ('\'' === currentChar) {
                                parserState = ParserState.SingleQuoteValue;
                                break;
                            }
                            keyvalue = this.getKeyValue(buffer, false);
                            parserState = ParserState.QuotedValueEnd;
                            throw DbConnectionStringBuilderService._parserStateEndError;

                        case ParserState.BraceQuoteValue: // "(\\{([^\\}\u0000]|\\}\\})*\\})"
                            if ('}' === currentChar) {
                                parserState = ParserState.BraceQuoteValueQuote;
                                break;
                            }
                            if ('\0' === currentChar) {
                                throw this.createConnectionStringSyntaxError(startposition);
                            }
                            break;

                        case ParserState.BraceQuoteValueQuote:
                            if ('}' === currentChar) {
                                parserState = ParserState.BraceQuoteValue;
                                break;
                            }
                            keyvalue = this.getKeyValue(buffer, false);
                            parserState = ParserState.QuotedValueEnd;
                            throw DbConnectionStringBuilderService._parserStateEndError;

                        case ParserState.QuotedValueEnd:
                            if (this.isWhiteSpace(currentChar)) {
                                continue;
                            }
                            if (';' === currentChar) {
                                throw DbConnectionStringBuilderService._parserExitError;
                            }
                            if ('\0' === currentChar) {
                                parserState = ParserState.NullTermination;
                                continue;
                            }
                            throw this.createConnectionStringSyntaxError(startposition);  // unbalanced single quote

                        case ParserState.NullTermination: // [\\s;\u0000]*
                            if ('\0' === currentChar) {
                                continue;
                            }
                            if (this.isWhiteSpace(currentChar)) {
                                continue;
                            }
                            throw this.createConnectionStringSyntaxError(currentPosition);

                        default:
                            throw new Error('Internal Error: InvalidParserState1');
                    }
                    buffer += currentChar;
                } catch (err) {
                    if (err === DbConnectionStringBuilderService._parserStateEndError) {
                        currentPosition--;
                    } else {
                        throw err;
                    }
                }
            }
        } catch (err) {
            if (err !== DbConnectionStringBuilderService._parserExitError) {
                throw err;
            }
        }

        switch (parserState) {
            case ParserState.Key:
            case ParserState.DoubleQuoteValue:
            case ParserState.SingleQuoteValue:
            case ParserState.BraceQuoteValue:
                // keyword not found/unbalanced double/single quote
                throw this.createConnectionStringSyntaxError(currentPosition);

            case ParserState.KeyEqual:
                // equal sign at end of line
                keyname = this.getKeyName(buffer);
                if (keyname == null || keyname === '') {
                    throw this.createConnectionStringSyntaxError(currentPosition);
                }
                break;

            case ParserState.UnquotedValue:
                // unquoted value at end of line
                keyvalue = this.getKeyValue(buffer, true);

                const tmpChar = keyvalue[keyvalue.length - 1];
                if (!useOdbcRules && (('\'' === tmpChar) || ('"' === tmpChar))) {
                    throw this.createConnectionStringSyntaxError(currentPosition);   // unquoted value must not end in quote, except for odbc
                }
                break;

            case ParserState.DoubleQuoteValueQuote:
            case ParserState.SingleQuoteValueQuote:
            case ParserState.BraceQuoteValueQuote:
            case ParserState.QuotedValueEnd:
                // quoted value at end of line
                keyvalue = this.getKeyValue(buffer, false);
                break;

            case ParserState.NothingYet:
            case ParserState.KeyEnd:
            case ParserState.NullTermination:
                // do nothing
                break;

            default:
                throw new Error('Internal Error: InvalidParserState2');
        }
        if ((';' === currentChar) && (currentPosition < connectionString.length)) {
            currentPosition++;
        }
        return {
            keyName: keyname,
            keyValue: keyvalue,
            position: currentPosition
        };
    }

    private getKeyName(buffer: string): string {
        let count = buffer.length;
        while ((0 < count) && this.isWhiteSpace(buffer[count - 1])) {
            count--; // trailing whitespace
        }
        // return buffer.substr(0, count).toLowerCase();
        return buffer.substr(0, count);
    }

    private getKeyValue(buffer: string, trimWhitespace: boolean): string {
        let count = buffer.length;
        let index = 0;
        if (trimWhitespace) {
            while ((index < count) && this.isWhiteSpace(buffer[index])) {
                index++; // leading whitespace
            }
            while ((0 < count) && this.isWhiteSpace(buffer[count - 1])) {
                count--; // trailing whitespace
            }
        }
        return buffer.substr(index, count - index);
    }

    private isWhiteSpace(char: string): boolean {
        return (char === '\x20') || (char >= '\x09' && char <= '\x0d') || char === '\xa0' || char === '\x85';
    }

    private isControl(char: string): boolean {
        return (char >= '\x00' && char <= '\x1f') || (char >= '\x7f' && char <= '\x9f');
    }

    private createConnectionStringSyntaxError(position: number): Error {
        return new Error(`Connection String Syntax Error, Position: ${position}`);
    }
}
